import { AuditTrailItemDto, BreakDto, UserShiftDto } from "@bluecrew/finance-service-node-client";
import union from "lodash/union";
import { AuditEditType, TransformedDuration, TransformedPair, TransformedPairOrDuration, } from "../types";
import { BreakTimestamps, ShiftThresholds, TimesheetsShiftInfo, } from "../../../../types/Shift.types";
import { getShiftBreakAttributes, getShiftTimestampAttributes, TimestampAttributeName } from "../../../../shiftUtils";
import { mapUserShift, mapUserTimestamp } from "../../../shiftMapperUtils";
import { PayrollHoursStatus } from "../../../../types/Payroll.types";
import { UserTimestamp } from "../../../../../../api/bluecrew/payroll";

/**
 * Factory class for creating audit edit objects
 * Centralizes all creation logic for TransformedEdit objects
 */
export class AuditEditFactory {
  /**
   * Utility function to safely convert lastUpdateBy to Date
   */
  static getUpdateDate(lastUpdateBy?: { updatedAt?: Date | string }): Date | undefined {
    if (!lastUpdateBy?.updatedAt) {
      return undefined;
    }
    return lastUpdateBy.updatedAt instanceof Date
      ? lastUpdateBy.updatedAt
      : new Date(lastUpdateBy.updatedAt);
  }

  /**
   * Helper method to get the most recent timestamp update time and author
   * @param timestamps List of timestamps to compare (usually start and end)
   */
  static getMostRecentTimestampUpdateInfo(
    timestamps: (UserTimestamp|undefined)[],
  ): { timestampsUpdatedAt?: Date; timestampsUpdatedBy?: string } {
    // Find the most recent updatedAt timestamp
    const mostRecentTimestamp = timestamps.reduce((latest, current) => {
      if (!latest) {
        return current;
      }
      if (!current) {
        return latest;
      }
      const currentUpdatedAt = new Date(current.updatedAt);
      const latestUpdatedAt = new Date(latest.updatedAt);
      return currentUpdatedAt > latestUpdatedAt ? current : latest;
    }, undefined);

    if (!mostRecentTimestamp) {
      return {};
    }
    return {
      timestampsUpdatedAt: new Date(mostRecentTimestamp.updatedAt),
      timestampsUpdatedBy: mostRecentTimestamp.updatedBy || undefined
    };
  }

  /**
   * Helper method to get an equivalent update time and author based on latest shift update
   * @param shiftInfo Current shift info (may include Legacy data)
   */
  static getUpdateInfoFromShiftInfo(
    shiftInfo: TimesheetsShiftInfo
  ): { timestampsUpdatedAt?: Date; timestampsUpdatedBy?: string } {
    return {
      timestampsUpdatedAt: this.getUpdateDate(shiftInfo.userShift.lastUpdateBy),
      timestampsUpdatedBy: shiftInfo.userShift.lastUpdateBy?.id
    }
  }

  /**
   * Maps edit description string from endpoint DTO to AuditEditType enum
   */
  static mapDtoDescriptionToType(description: string): AuditEditType {
    switch (description) {
      case "Crew member":
        return AuditEditType.CREW_MEMBER;
      case "Client":
        return AuditEditType.CLIENT;
      case "BlueCrew support":
        return AuditEditType.BLUECREW_SUPPORT;
      case AuditEditType.ORIGINAL:
        return AuditEditType.ORIGINAL;
      case AuditEditType.CLIENT_SUPPORT:
        return AuditEditType.CLIENT_SUPPORT;
      default:
        return AuditEditType.CREW_MEMBER;
    }
  }

  /**
   * Creates a break edit from a break timestamp with common fields
   */
  static createBreakFromTimestamps(
    breakTimestamp: BreakDto,
    editUserShift: UserShiftDto,
    dtoDescription: string,
    editTimestamp: Date | 'from-user-shift',
    shiftInfo:  TimesheetsShiftInfo,
  ) : TransformedPair {
    const mappedUserShift = mapUserShift(editUserShift);
    const mappedBreakStart = breakTimestamp.clockOut && mapUserTimestamp(breakTimestamp.clockOut) || undefined;
    const mappedBreakEnd = breakTimestamp.clockIn && mapUserTimestamp(breakTimestamp.clockIn) ||  undefined;

    let startAttributes = mappedUserShift.break.attributes;
    let endAttributes = mappedUserShift.break.attributes;
    if (dtoDescription === AuditEditType.ORIGINAL) {
      // Recalculate attributes for each individual break element
      // until the Audit Trail endpoint is able to calculate it for itself
      startAttributes = union(startAttributes, this.calculateExtraAttributes(mappedBreakStart, shiftInfo.shiftThresholds));
      endAttributes = union(endAttributes, this.calculateExtraAttributes(mappedBreakEnd, shiftInfo.shiftThresholds));
    } else {
      // For edits, the CM_EDITED_SUBMISSION flag is coming if ANY of the breaks is edited
      // This checks each break element individually
      startAttributes = startAttributes.filter(attr => attr !== TimestampAttributeName.CM_EDITED_SUBMISSION || mappedBreakStart?.isUserEdited);
      endAttributes = endAttributes.filter(attr => attr !== TimestampAttributeName.CM_EDITED_SUBMISSION || mappedBreakEnd?.isUserEdited);
    }

    // Get timestamp update information
    const updateInfo = this.getMostRecentTimestampUpdateInfo([mappedBreakStart, mappedBreakEnd]);

    return {
      editType: AuditEditFactory.mapDtoDescriptionToType(dtoDescription),
      updateDate: editTimestamp !== 'from-user-shift' ? new Date(editTimestamp) : AuditEditFactory.getUpdateDate(mappedUserShift.lastUpdateBy),
      updateUser: mappedUserShift.lastUpdateBy,
      start: breakTimestamp.clockOut?.time ? new Date(breakTimestamp.clockOut.time) : undefined,
      end: breakTimestamp.clockIn?.time ? new Date(breakTimestamp.clockIn.time) : undefined,
      startAttributes,
      endAttributes,
      deleted: false,
      ...updateInfo,
    };
  }

  static createBreakFromPrevious(
    lastBreak: TransformedPair,
    editUserShift: UserShiftDto,
    dtoDescription: string,
    editTimestamp: Date,
    isDeleted: boolean,
  ): TransformedPair {
    const mappedUserShift = mapUserShift(editUserShift);
    return {
      editType: AuditEditFactory.mapDtoDescriptionToType(dtoDescription),
      updateDate: new Date(editTimestamp),
      updateUser: mappedUserShift.lastUpdateBy,
      start: lastBreak.start,
      end: lastBreak.end,
      startAttributes: lastBreak.startAttributes,
      endAttributes: lastBreak.endAttributes,
      deleted: isDeleted,
      // Ideally, timestampsUpdatedAt and timestampsUpdatedBy should be the time and author of the deletion
      // Unfortunately I don't have the data here so I'll use the previous one and check for deletion in isActualEdit()
      timestampsUpdatedAt: lastBreak.timestampsUpdatedAt,
      timestampsUpdatedBy: lastBreak.timestampsUpdatedBy,
    };
  }

  /**
   * Creates a legacy break edit with duration calculation for ORIGINAL and CLIENT_SUPPORT types
   * Ensures duration is always present for legacy path TransformedEdits
   */
  static createLegacyBreak(
    shiftInfo: TimesheetsShiftInfo,
    editUserShift: UserShiftDto | 'from-shift-info',
    dtoDescription: string,
    editTimestamp: Date | 'from-user-shift',
  ): TransformedDuration {
    const mappedUserShift = editUserShift === 'from-shift-info'
      ? shiftInfo.userShift : mapUserShift(editUserShift);

    // Regenerate attributes for the original case
    let breakAttributes = editUserShift === 'from-shift-info' ? [] : mappedUserShift.break.attributes;
    if (dtoDescription === AuditEditType.ORIGINAL) {
      breakAttributes = union(
        breakAttributes,
        this.calculateExtraLegacyBreakAttributes(mappedUserShift.break.breakTimestamps, shiftInfo.shiftThresholds),
      );
    }

    const updateInfo = editUserShift === 'from-shift-info'
      ? this.getUpdateInfoFromShiftInfo(shiftInfo)
      : this.getMostRecentTimestampUpdateInfo(mappedUserShift.break.breakTimestamps.flatMap(brk => [brk.start, brk.end]))

    return {
      editType: AuditEditFactory.mapDtoDescriptionToType(dtoDescription),
      updateDate: editTimestamp === 'from-user-shift' ? AuditEditFactory.getUpdateDate(mappedUserShift.lastUpdateBy) : new Date(editTimestamp),
      updateUser: mappedUserShift.lastUpdateBy,
      attributes: breakAttributes,
      breakDurationMinutes: mappedUserShift.break.durationMinutes || 0,
      deleted: false,
      ...updateInfo,
    };
  }

  /**
   * Creates an original clock in/out edit from original data or fallback to shift info
   */
  static createOriginalClockInOut(
    original: UserShiftDto | undefined,
    shiftInfo: TimesheetsShiftInfo,
  ): TransformedPair {
    if (original) {
      const mappedOriginal = mapUserShift(original);

      // I've could have used the same idea of copying attributes from timesheets
      // but this will be more consistent with what's being done with breaks
      // (until the Audit Trail endpoint is able to calculate the attributes itself)
      const extraStartAttributes = this.calculateExtraAttributes(mappedOriginal.start.userTimestamp, shiftInfo.shiftThresholds);
      const extraEndAttributes = this.calculateExtraAttributes(mappedOriginal.end.userTimestamp, shiftInfo.shiftThresholds);

      const updateInfo = this.getMostRecentTimestampUpdateInfo([mappedOriginal.start.userTimestamp, mappedOriginal.end.userTimestamp]);

      return {
        editType: AuditEditType.ORIGINAL,
        updateDate: this.getUpdateDate(mappedOriginal.lastUpdateBy),
        updateUser: mappedOriginal.lastUpdateBy,
        start: new Date(mappedOriginal.start.time),
        end: new Date(mappedOriginal.end.time),
        startAttributes: union(mappedOriginal.start.attributes, extraStartAttributes),
        endAttributes: union(mappedOriginal.end.attributes, extraEndAttributes),
        deleted: false,
        ...updateInfo,
      };
    }

    return {
      editType: AuditEditType.ORIGINAL,
      updateDate: this.getUpdateDate(shiftInfo.userShift.lastUpdateBy),
      updateUser: shiftInfo.userShift.lastUpdateBy,
      start: new Date(shiftInfo.job.start),
      end: new Date(shiftInfo.job.end),
      startAttributes: [TimestampAttributeName.CM_MISSING_SUBMISSION],
      endAttributes: [TimestampAttributeName.CM_MISSING_SUBMISSION],
      deleted: false,
      timestampsUpdatedBy: undefined,
      timestampsUpdatedAt: undefined,
    };
  }

  /**
   * Creates a clock in/out edit from an audit trail item
   */
  static createClockInOutFromEdit(
    edit: AuditTrailItemDto,
  ): TransformedPair | null {
    if (!edit.userShift || !edit.editTimestamp) {
      return null;
    }

    const mappedUserShift = mapUserShift(edit.userShift);
    const updateInfo = this.getMostRecentTimestampUpdateInfo(
      [mappedUserShift.start.userTimestamp, mappedUserShift.end.userTimestamp]
    );

    return {
      editType: this.mapDtoDescriptionToType(edit.editDescription),
      updateDate: new Date(edit.editTimestamp),
      updateUser: mappedUserShift.lastUpdateBy,
      start: new Date(mappedUserShift.start.time),
      end: new Date(mappedUserShift.end.time),
      startAttributes: mappedUserShift.start.attributes,
      endAttributes: mappedUserShift.end.attributes,
      deleted: false,
      ...updateInfo,
    };
  }

  /**
   * Creates a CLIENT_SUPPORT clock in/out edit from shift info
   */
  static createClientSupportClockInOut(shiftInfo: TimesheetsShiftInfo): TransformedPair {
    return {
      editType: AuditEditType.CLIENT_SUPPORT,
      updateDate: this.getUpdateDate(shiftInfo.userShift.lastUpdateBy),
      updateUser: shiftInfo.userShift.lastUpdateBy,
      start: new Date(shiftInfo.userShift.start.time),
      end: new Date(shiftInfo.userShift.end.time),
      startAttributes: this.filterClientSupportAttributes(shiftInfo.userShift.start.attributes),
      endAttributes: this.filterClientSupportAttributes(shiftInfo.userShift.end.attributes),
      deleted: false,
      ...this.getUpdateInfoFromShiftInfo(shiftInfo)
    };
  }

  private static calculateExtraAttributes(userTimestamp: UserTimestamp | undefined, shiftThresholds: ShiftThresholds) {
    const attributes = getShiftTimestampAttributes(
      shiftThresholds,
      PayrollHoursStatus.NEEDS_VERIFICATION,
      userTimestamp,
    );
    // Filter out CM_EDITED_SUBMISSION as per FIN-610
    // but also removing CM_MISSING_SUBMISSION as it will come from backend
    return attributes.filter(attr =>
      attr !== TimestampAttributeName.CM_EDITED_SUBMISSION &&
      attr !== TimestampAttributeName.CM_MISSING_SUBMISSION
    );
  }

  private static calculateExtraLegacyBreakAttributes(breaks: BreakTimestamps[], shiftThresholds: ShiftThresholds) {
    const attributes = getShiftBreakAttributes(
      shiftThresholds,
      breaks,
      PayrollHoursStatus.NEEDS_VERIFICATION
    );
    // The current version of getShiftBreakAttributes adds a TIME_ERROR validation
    // if the total break duration is less than shiftThresholds.minLunchDurationHours
    // That logic has been removed from Finance Service, so I filter it out here
    // to match what is currently shown in the Audit
    // TODO: check if we can remove that logic from getShiftBreakAttributes directly
    return attributes.filter(attr => attr !== TimestampAttributeName.TIME_ERROR);
  }

  private static filterClientSupportAttributes(attributes: TimestampAttributeName[]) {
    // In the previous version the client/support part didn't show any attributes,
    // but with the original timestamps now recalculating its values based on them
    // instead of using the attributes from the table, this section will need to show
    // any attributes from the table that may not be now in the original timestamps
    // (and that in the previous version were accidentally shown in the original timestamps part)
    // We need to filter though:
    // -- GEOFENCE_ERROR and GEOFENCE_WARNING (they will appear in the original timestamps)
    // -- CM_EDITED_SUBMISSION (they will appear in CM edits)
    // -- TIME_ERROR (should not be shown in edits)
    return attributes.filter(attr =>
      attr !== TimestampAttributeName.GEOFENCE_ERROR &&
      attr !== TimestampAttributeName.GEOFENCE_WARNING &&
      attr !== TimestampAttributeName.CM_EDITED_SUBMISSION &&
      attr !== TimestampAttributeName.TIME_ERROR
    );
  }

  /**
   * Determines if an edit represents an actual change by comparing with the previous entry
   */
  static isActualEdit(current: TransformedPairOrDuration, previous?: TransformedPairOrDuration): boolean
  {
    if (!previous || current.editType === AuditEditType.CLIENT_SUPPORT) {
      // First entry is always considered an edit (original timestamp)
      return true;
    }

    // Compare using timestampsUpdatedAt and timestampsUpdatedBy to detect actual changes
    // Compare also if delete status has changed (due to limitations on createBreakFromPrevious())
    const timeChanged = current.timestampsUpdatedAt?.getTime() !== previous.timestampsUpdatedAt?.getTime();
    const userChanged = current.timestampsUpdatedBy !== previous.timestampsUpdatedBy;
    const differentDeleteStatus = current.deleted !== previous.deleted;

    return timeChanged || userChanged || differentDeleteStatus;
  }
}
